/**
 * The level as the rules see it (spec 03): solid tiles and the bubble current, by tile, with the
 * C64's coordinate conversions. Everything outside the 32x25 grid is empty, and the wind there is
 * that of the nearest cell.
 */

import type { LevelDef } from '../levels/types';

export const COLS = 32;
export const ROWS = 25;

export type Wind = 'up' | 'right' | 'down' | 'left';
const WIND: Record<string, Wind> = { '^': 'up', '>': 'right', v: 'down', '<': 'left' };

export interface Playfield {
  /** Row-major, 1 = solid. */
  readonly solid: readonly number[];
  readonly wind: readonly Wind[];
}

export function playfieldOf(level: LevelDef): Playfield {
  const solid: number[] = [];
  const wind: Wind[] = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      solid.push(level.tiles[r]?.[c] === '#' ? 1 : 0);
      wind.push(WIND[level.wind[r]?.[c] ?? '^'] ?? 'up');
    }
  }
  return { solid, wind };
}

/** Is the tile at (col, row) solid? Outside the grid is empty. */
export function solidTile(p: Playfield, col: number, row: number): boolean {
  if (col < 0 || row < 0 || col >= COLS || row >= ROWS) return false;
  return p.solid[row * COLS + col] === 1;
}

/** The bubble current at (col, row), clamped into the grid. */
export function windAt(p: Playfield, col: number, row: number): Wind {
  const c = Math.max(0, Math.min(COLS - 1, col));
  const r = Math.max(0, Math.min(ROWS - 1, row));
  return p.wind[r * COLS + c] ?? 'up';
}

/** Tile column under a sprite's body (VIC X): (X - 20) / 8 (02 §2). */
export const spriteCol = (x: number): number => Math.floor((x - 20) / 8);
/** Top tile row of a sprite's body (VIC Y): (Y - 45) / 8 (02 §2). */
export const spriteRow = (y: number): number => Math.floor((y - 45) / 8);
/** VIC Y of a body standing on tile row `row` (its feet on that row's top). */
export const standingY = (row: number): number => 45 + 8 * (row - 2);
